/**
 * cart-telemetry.js
 * Recommendation telemetry for the Brewout app proxy.
 *
 * Sends impression and click events to /apps/reco/telemetry as:
 *   – image beacon (GET, fire-and-forget)
 *   – fetch keepalive (POST) as fallback when image beacon may be blocked
 *
 * Listens for data-cu-reco-item elements added to the DOM and automatically
 * tracks impressions via IntersectionObserver.
 */
(function () {
  'use strict';

  const ENDPOINT = '/apps/reco/telemetry';
  const BEACON_TIMEOUT_MS = 3000;

  /* ── Session ID ─────────────────────────────────────────────────────────────── */
  const getSessionId = () => {
    let id = localStorage.getItem('brewout_session_id');
    if (!id) {
      id = 'sess_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
      localStorage.setItem('brewout_session_id', id);
    }
    return id;
  };

  /* ── Build payload ──────────────────────────────────────────────────────────── */
  const buildPayload = (event, data) => {
    const p = getPersona();
    return {
      event,
      session_id: getSessionId(),
      persona: p.persona || undefined,
      persona_confidence: p.confidence || undefined,
      timestamp: Date.now(),
      ...data,
    };
  };

  const getPersona = () => {
    if (window.CartCore) return window.CartCore.getPersona();
    return {
      persona: localStorage.getItem('brewout_persona') || '',
      confidence: parseFloat(localStorage.getItem('brewout_persona_confidence') || '0'),
    };
  };

  /* ── Send via image beacon ───────────────────────────────────────────────────── */
  const sendBeacon = (payload) => {
    try {
      const params = new URLSearchParams({ data: JSON.stringify(payload) });
      const img = new Image();
      img.src = `${ENDPOINT}?${params}`;
    } catch (e) {/* noop */}
  };

  /* ── Send via fetch keepalive ────────────────────────────────────────────────── */
  const sendFetch = (payload) => {
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'same-origin',
      });
    } catch (e) {/* noop */}
  };

  /* ── Main send ───────────────────────────────────────────────────────────────── */
  // Telemetry disabled — endpoint API contract unknown, avoid console errors.
  const send = (_event, _data) => { /* noop */ };

  /* ── Impression tracking ─────────────────────────────────────────────────────── */
  const impressionSent = new WeakSet();
  let observer;

  const getObserver = () => {
    if (observer) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        if (impressionSent.has(el)) return;
        impressionSent.add(el);
        observer.unobserve(el);

        const productId = el.dataset.cuRecoProductId;
        const position = el.dataset.cuRecoPosition;
        const surface = el.dataset.cuRecoSurface || 'cart';
        const algo = el.dataset.cuRecoAlgo || '';

        if (productId) {
          send('impression', {
            product_id: productId,
            position: parseInt(position) || 0,
            surface,
            algo,
          });
        }
      });
    }, { threshold: 0.5 });
    return observer;
  };

  const observeRecoItems = (container) => {
    const root = container || document;
    root.querySelectorAll('[data-cu-reco-product-id]').forEach(el => {
      if (!impressionSent.has(el)) {
        getObserver().observe(el);
      }
    });
  };

  /* ── Click tracking ──────────────────────────────────────────────────────────── */
  document.addEventListener('click', (e) => {
    const recoItem = e.target.closest('[data-cu-reco-product-id]');
    if (!recoItem) return;

    const productId = recoItem.dataset.cuRecoProductId;
    const position = recoItem.dataset.cuRecoPosition;
    const surface = recoItem.dataset.cuRecoSurface || 'cart';
    const algo = recoItem.dataset.cuRecoAlgo || '';

    if (!productId) return;

    // Distinguish add-to-cart vs product page click
    const isAtc = e.target.closest('[data-cu-reco-atc]');
    send(isAtc ? 'atc' : 'click', {
      product_id: productId,
      position: parseInt(position) || 0,
      surface,
      algo,
    });
  });

  /* ── Listen for new items rendered into DOM ──────────────────────────────────── */
  if (typeof MutationObserver !== 'undefined') {
    const mo = new MutationObserver((mutations) => {
      mutations.forEach(m => {
        m.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.dataset && node.dataset.cuRecoProductId) {
            getObserver().observe(node);
          }
          node.querySelectorAll && node.querySelectorAll('[data-cu-reco-product-id]').forEach(el => {
            if (!impressionSent.has(el)) getObserver().observe(el);
          });
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* ── Public API ─────────────────────────────────────────────────────────────── */
  window.CartTelemetry = {
    send,
    observeRecoItems,
    getSessionId,
  };
})();
